import React from 'react';
import { NavLink, Outlet, Navigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { User, CreditCard, Store, Wallet } from 'lucide-react';

const navItems = [
  { href: '/settings/account', label: 'Account', icon: User },
  { href: '/settings/workspace', label: 'Studio & Team', icon: Store },
  { href: '/settings/billing', label: 'Membership', icon: CreditCard },
  { href: '/settings/payouts', label: 'Payouts', icon: Wallet },
];

const SettingsPage: React.FC = () => {
  const location = useLocation();

  // Redirect /settings to /settings/account
  if (location.pathname === '/settings') {
      return <Navigate to="/settings/account" replace />;
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 md:px-8">
      <div className="mb-10 text-center md:text-left">
        <p className="mb-4 inline-flex rounded-full border border-slate-200 bg-white px-4 py-2 text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-slate-600">
          Workspace Settings
        </p>
        <h1 className="mb-3 text-4xl font-bold tracking-[-0.04em] text-slate-950 md:text-5xl">Settings</h1>
        <p className="text-lg tracking-[-0.01em] text-slate-600">Manage your studio, team, and billing.</p>
      </div>

      <div className="flex flex-col md:flex-row gap-8 md:gap-12">
        {/* Navigation */}
        <aside className="w-full md:w-64 shrink-0">
          <nav className="flex gap-2 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-sm md:flex-col md:overflow-visible md:pb-0 no-scrollbar">
            {navItems.map(item => (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) => cn(
                  "flex shrink-0 items-center gap-3 whitespace-nowrap rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200",
                  isActive 
                    ? "bg-slate-900 text-white shadow-[0_12px_22px_-16px_rgba(15,23,42,0.85)]" 
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                )}
              >
                <item.icon size={18} />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Content Area */}
        <main className="min-w-0 flex-1 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default SettingsPage;
